import { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, Check, Car, User, Phone, Palette, Sparkles, CheckCircle2, Calendar as CalendarIcon, Clock, Star, Gift, ShoppingCart, CreditCard, Package, MessageSquare, Info, Tag, Loader2, X, Percent } from "lucide-react";
import { validateCoupon } from "@/lib/firestore";
import { CouponType, type Coupon } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn, normalizePhoneNumber, isValidTurkishPhone, formatCurrency } from "@/lib/utils";
import { ServiceCategory, type Service } from "@shared/schema";
import { format, isAfter, addDays, startOfToday } from "date-fns";
import { tr } from "date-fns/locale";

const VEHICLE_TYPES = [
  { value: 'sedan', label: 'Binek', icon: '🚗', description: 'Sedan, Hatchback' },
  { value: 'suv', label: 'SUV', icon: '🚙', description: 'SUV, Crossover' },
  { value: 'large', label: 'Büyük Araç', icon: '🚐', description: 'Minivan, Van' },
] as const;

interface BookingWizardProps {
  isGuest: boolean;
  customerId?: string | null;
  existingVehicles?: Array<{
    id: string;
    plate: string;
    brand: string;
    model: string;
    color?: string;
    type: 'sedan' | 'suv' | 'large';
  }>;
  services?: Service[];
  getAvailableSlots: (date: Date) => string[];
  onDateChange?: (date: Date) => void;
  activeSubscriptions?: Array<{
    id: string;
    packageName: string;
    remainingExteriorWashes: number;
    remainingInteriorCleans: number;
    remainingDetailings: number;
    remainingExtraServices: number;
    expiresAt: string;
  }>;
  onComplete: (data: BookingFormData) => void;
  onCancel: () => void;
  defaultName?: string;
  defaultPhone?: string;
  phoneVerified?: boolean;
}

export interface BookingFormData {
  vehicleId?: string;
  plate: string;
  type: 'sedan' | 'suv' | 'large';
  brand: string;
  model: string;
  color: string;
  customerName: string;
  customerPhone: string;
  selectedServiceIds: string[];
  selectedDate: Date;
  selectedTime: string;
  subscriptionId?: string | null;
  useSubscription?: boolean;
  couponCode?: string | null;
  couponId?: string | null;
  couponType?: 'percentage' | 'free_service' | null;
  couponDiscountPercentage?: number | null;
  couponFreeServiceId?: string | null;
  discountAmount?: number;
}

export function BookingWizard({
  isGuest,
  customerId,
  existingVehicles = [],
  services = [],
  getAvailableSlots,
  onDateChange,
  activeSubscriptions = [],
  onComplete,
  onCancel,
  defaultName = "",
  defaultPhone = "",
  phoneVerified = false,
}: BookingWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<BookingFormData>>({
    vehicleId: undefined,
    plate: '',
    type: 'sedan',
    brand: '',
    model: '',
    color: '',
    customerName: defaultName,
    customerPhone: defaultPhone,
    selectedServiceIds: [],
    selectedDate: undefined,
    selectedTime: '',
    subscriptionId: null,
    useSubscription: false,
    couponCode: null,
    couponId: null,
    couponType: null,
    couponDiscountPercentage: null,
    couponFreeServiceId: null,
    discountAmount: 0,
  });
  
  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponApplied, setCouponApplied] = useState(false);
  
  // SMS Verification state
  const [verificationCode, setVerificationCode] = useState("");
  const [isSmsSending, setIsSmsSending] = useState(false);
  const [isSmsSent, setIsSmsSent] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  
  // Service Details Dialog
  const [serviceDetailDialogOpen, setServiceDetailDialogOpen] = useState(false);
  const [selectedServiceForDetails, setSelectedServiceForDetails] = useState<typeof services[0] | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<string>("popular");
  const [useExistingVehicle, setUseExistingVehicle] = useState<boolean>(existingVehicles.length > 0);
  
  // Calculate available slots based on selected date
  const availableSlots = formData.selectedDate ? getAvailableSlots(formData.selectedDate) : [];
  
  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);
  
  // Dynamic steps based on user type and vehicle selection
  const getSteps = () => {
    const steps = [];
    
    // Step: Vehicle selection (for registered users with vehicles)
    if (existingVehicles.length > 0) {
      steps.push({ id: 'vehicle-select', title: 'Araç Seçimi', description: 'Araç seçin' });
    }
    
    // Steps: New vehicle info (if adding new vehicle or guest)
    if (!useExistingVehicle || existingVehicles.length === 0) {
      steps.push(
        { id: 'vehicle-type', title: 'Araç Tipi', description: 'Araç kategorisi' },
        { id: 'vehicle-plate', title: 'Plaka', description: 'Araç plakası' },
        { id: 'vehicle-brand', title: 'Marka', description: 'Araç markası' },
        { id: 'vehicle-model', title: 'Model', description: 'Araç modeli' },
        { id: 'vehicle-color', title: 'Renk', description: 'Araç rengi' }
      );
    }
    
    // Steps: Contact info
    // Guest users: Always ask for name and phone
    // Registered users: Only ask for phone if not provided (e.g., Gmail users)
    if (isGuest) {
      steps.push(
        { id: 'customer-name', title: 'Adınız', description: 'İsim Soyisim' },
        { id: 'customer-phone', title: 'Telefon', description: 'İletişim numarası' }
      );
    } else if (!defaultPhone || defaultPhone.trim() === '') {
      // Registered user without phone number (e.g., Gmail login)
      steps.push(
        { id: 'customer-phone', title: 'Telefon', description: 'İletişim numarası' }
      );
    }
    
    // Steps: Service, Date, Time
    steps.push(
      { id: 'services', title: 'Hizmetler', description: 'Yıkama hizmetleri' },
      { id: 'date', title: 'Tarih', description: 'Randevu tarihi' },
      { id: 'time', title: 'Saat', description: 'Randevu saati' }
    );
    
    // Step: SMS Verification (for guest users or users with unverified phones)
    // This step MUST be LAST before completion
    // Skip if: registered user with phone already set (phoneVerified OR has defaultPhone)
    // Rationale: If user has phone in their profile, they've already verified it once
    if (isGuest || (!phoneVerified && !defaultPhone)) {
      steps.push({ id: 'sms-verification', title: 'Doğrulama', description: 'Telefon doğrulama' });
    }
    
    return steps;
  };

  const STEPS = getSteps();
  const progress = (currentStep / STEPS.length) * 100;
  
  // Reset step when vehicle selection mode changes and current step becomes invalid
  useEffect(() => {
    if (currentStep > STEPS.length) {
      // Current step is out of bounds, reset to first step after vehicle selection
      if (existingVehicles.length > 0 && useExistingVehicle) {
        // User selected existing vehicle, go to next step after selection
        setCurrentStep(2); // Step after vehicle-select
      } else {
        setCurrentStep(1); // Reset to first step
      }
    }
  }, [useExistingVehicle, STEPS.length, currentStep, existingVehicles.length]);
  
  // Helper to get current step ID
  const getCurrentStepId = () => STEPS[currentStep - 1]?.id;
  
  // Helper to find step index by ID
  const getStepIndexById = (id: string) => {
    const index = STEPS.findIndex(step => step.id === id);
    return index >= 0 ? index + 1 : 1;
  };

  const updateField = <K extends keyof BookingFormData>(field: K, value: BookingFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectVehicle = (vehicle: typeof existingVehicles[0]) => {
    setFormData(prev => ({
      ...prev,
      vehicleId: vehicle.id,
      plate: vehicle.plate,
      brand: vehicle.brand,
      model: vehicle.model,
      color: vehicle.color || '',
      type: vehicle.type,
    }));
    setUseExistingVehicle(true);
    
    // Skip to contact info if guest, to phone if no phone, otherwise to services
    if (isGuest) {
      setCurrentStep(getStepIndexById('customer-name'));
    } else if (!defaultPhone || defaultPhone.trim() === '') {
      setCurrentStep(getStepIndexById('customer-phone'));
    } else {
      setCurrentStep(getStepIndexById('services'));
    }
  };

  const handleNewVehicle = () => {
    setUseExistingVehicle(false);
    setFormData(prev => ({
      ...prev,
      vehicleId: undefined,
      plate: '',
      brand: '',
      model: '',
      color: '',
      type: 'sedan',
    }));
    setCurrentStep(getStepIndexById('vehicle-type'));
  };

  const canProgress = (): boolean => {
    const stepId = getCurrentStepId();
    switch (stepId) {
      case 'vehicle-select': return true;
      case 'vehicle-type': return !!formData.type;
      case 'vehicle-plate': return (formData.plate?.trim().length ?? 0) >= 2;
      case 'vehicle-brand': return (formData.brand?.trim().length ?? 0) >= 2;
      case 'vehicle-model': return (formData.model?.trim().length ?? 0) >= 2;
      case 'vehicle-color': return true; // Color is optional
      case 'customer-name': return (formData.customerName?.trim().length ?? 0) >= 3;
      case 'customer-phone': return isValidTurkishPhone(formData.customerPhone || '');
      case 'services': {
        // Services must be selected
        const hasServices = (formData.selectedServiceIds?.length ?? 0) > 0;
        // If subscription toggle is ON, a subscription must be selected
        const subscriptionValid = !formData.useSubscription || !!formData.subscriptionId;
        return hasServices && subscriptionValid;
      }
      case 'date': return !!formData.selectedDate;
      case 'time': return !!formData.selectedTime;
      case 'sms-verification': return verificationCode.length === 6;
      default: return false;
    }
  };

  const handleNext = () => {
    if (canProgress() && currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSendSms = async () => {
    if (!formData.customerPhone) return;
    
    setIsSmsSending(true);
    setSmsError(null);
    
    try {
      const { sendVerificationSms } = await import("@/lib/cloudFunctions");
      const result = await sendVerificationSms({
        phoneNumber: formData.customerPhone,
        name: formData.customerName || defaultName || "Müşteri",
      });
      
      if (result.success) {
        setIsSmsSent(true);
      } else {
        setSmsError(result.message || "SMS gönderilemedi");
      }
    } catch (error: any) {
      setSmsError(error.message || "Bir hata oluştu");
    } finally {
      setIsSmsSending(false);
    }
  };

  const handleComplete = () => {
    if (canProgress()) {
      const completedData = {
        ...formData,
        verificationCode: verificationCode || undefined,
        discountAmount: discountAmount,
      } as BookingFormData & { verificationCode?: string };
      
      onComplete(completedData);
    }
  };

  const formatPhoneInput = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 4) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 4)} ${numbers.slice(4)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7)}`;
    return `${numbers.slice(0, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7, 9)} ${numbers.slice(9, 11)}`;
  };

  const toggleService = (serviceId: string) => {
    const current = formData.selectedServiceIds || [];
    if (current.includes(serviceId)) {
      updateField('selectedServiceIds', current.filter(id => id !== serviceId));
    } else {
      updateField('selectedServiceIds', [...current, serviceId]);
    }
  };

  const filteredServices = services.filter(service => {
    if (!service.active) return false;
    const matchesCategory = selectedCategory === "popular"
      ? service.isPopular
      : service.category === selectedCategory;
    return matchesCategory;
  });

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    
    setCouponLoading(true);
    setCouponError(null);
    
    try {
      const result = await validateCoupon(couponInput.trim(), customerId);
      
      if (!result.isValid || !result.coupon) {
        setCouponError(result.error || "Gecersiz kupon kodu");
        return;
      }
      
      const coupon = result.coupon;
      
      if (coupon.type === CouponType.FREE_SERVICE && coupon.freeServiceId) {
        const freeService = services.find(s => s.id === coupon.freeServiceId);
        if (!freeService) {
          setCouponError("Kupondaki hizmet bulunamadi");
          return;
        }
        
        const currentServices = formData.selectedServiceIds || [];
        if (!currentServices.includes(coupon.freeServiceId)) {
          updateField('selectedServiceIds', [...currentServices, coupon.freeServiceId]);
        }
        
        setFormData(prev => ({
          ...prev,
          couponCode: coupon.code,
          couponId: coupon.id,
          couponType: 'free_service',
          couponDiscountPercentage: null,
          couponFreeServiceId: coupon.freeServiceId,
        }));
      } else if (coupon.type === CouponType.PERCENTAGE) {
        setFormData(prev => ({
          ...prev,
          couponCode: coupon.code,
          couponId: coupon.id,
          couponType: 'percentage',
          couponDiscountPercentage: coupon.discountPercentage,
          couponFreeServiceId: null,
        }));
      }
      
      setCouponApplied(true);
    } catch (error: any) {
      setCouponError(error.message || "Kupon dogrulanamadi");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    if (formData.couponFreeServiceId) {
      const currentServices = formData.selectedServiceIds || [];
      updateField('selectedServiceIds', currentServices.filter(id => id !== formData.couponFreeServiceId));
    }
    
    setFormData(prev => ({
      ...prev,
      couponCode: null,
      couponId: null,
      couponType: null,
      couponDiscountPercentage: null,
      couponFreeServiceId: null,
      discountAmount: 0,
    }));
    setCouponApplied(false);
    setCouponInput("");
    setCouponError(null);
  };

  const freeServicePrice = formData.couponFreeServiceId && formData.type
    ? services.find(s => s.id === formData.couponFreeServiceId)?.pricing[formData.type] || 0
    : 0;

  const subtotalPrice = formData.useSubscription 
    ? 0 
    : (formData.selectedServiceIds || []).reduce((sum, serviceId) => {
        const service = services.find(s => s.id === serviceId);
        if (!service || !formData.type) return sum;
        return sum + service.pricing[formData.type];
      }, 0);

  const discountAmount = formData.couponType === 'percentage' && formData.couponDiscountPercentage
    ? Math.round((subtotalPrice - freeServicePrice) * (formData.couponDiscountPercentage / 100))
    : formData.couponType === 'free_service'
      ? freeServicePrice
      : 0;

  const totalPrice = Math.max(0, subtotalPrice - discountAmount);
  
  const freeServiceName = formData.couponFreeServiceId 
    ? services.find(s => s.id === formData.couponFreeServiceId)?.name 
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex flex-col">
      {/* Header with Progress */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={currentStep === 1 ? onCancel : handlePrevious}
              className="hover:bg-primary/10"
              data-testid="button-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            <div className="text-center flex-1 mx-4">
              <h2 className="text-lg font-bold text-foreground">
                {STEPS[currentStep - 1]?.title}
              </h2>
              <p className="text-xs text-muted-foreground">
                {STEPS[currentStep - 1]?.description}
              </p>
            </div>
            
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Adım {currentStep} / {STEPS.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          
          {/* Step: Vehicle Selection */}
          {getCurrentStepId() === 'vehicle-select' && existingVehicles.length > 0 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="text-center mb-6">
                <p className="text-lg text-muted-foreground">
                  Araç seçin veya yeni araç ekleyin
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {existingVehicles.map((vehicle) => (
                  <Card
                    key={vehicle.id}
                    onClick={() => handleSelectVehicle(vehicle)}
                    className="p-6 cursor-pointer transition-all hover:shadow-xl hover:scale-105 active:scale-95 hover:border-primary/50"
                    data-testid={`vehicle-card-${vehicle.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-4xl">
                        {VEHICLE_TYPES.find(t => t.value === vehicle.type)?.icon || '🚗'}
                      </div>
                      <div className="flex-1">
                        <div className="text-2xl font-bold mb-1">{vehicle.plate}</div>
                        <div className="text-sm text-muted-foreground">
                          {vehicle.brand} {vehicle.model}
                        </div>
                        {vehicle.color && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {vehicle.color}
                          </div>
                        )}
                      </div>
                      <ArrowRight className="w-6 h-6 text-muted-foreground" />
                    </div>
                  </Card>
                ))}
              </div>
              
              <Card
                onClick={handleNewVehicle}
                className="p-8 cursor-pointer transition-all hover:shadow-xl hover:scale-105 active:scale-95 border-dashed border-2 hover:border-primary"
                data-testid="button-new-vehicle"
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Car className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Yeni Araç Ekle</h3>
                    <p className="text-sm text-muted-foreground">
                      Farklı bir araç için randevu alın
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Step: Vehicle Type */}
          {getCurrentStepId() === 'vehicle-type' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {VEHICLE_TYPES.map((type) => (
                  <Card
                    key={type.value}
                    onClick={() => updateField('type', type.value)}
                    className={cn(
                      "p-8 sm:p-10 cursor-pointer transition-all hover:shadow-xl hover:scale-105 active:scale-95",
                      formData.type === type.value
                        ? "border-primary border-2 bg-primary/5 shadow-lg"
                        : "hover:border-primary/50"
                    )}
                    data-testid={`vehicle-type-${type.value}`}
                  >
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="text-5xl sm:text-6xl mb-2">{type.icon}</div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-bold mb-1">{type.label}</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {type.description}
                        </p>
                      </div>
                      {formData.type === type.value && (
                        <CheckCircle2 className="w-8 h-8 text-primary animate-in zoom-in duration-200" />
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Step: Plate */}
          {getCurrentStepId() === 'vehicle-plate' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-8">
              <Card className="p-6 sm:p-12">
                <div className="flex flex-col items-center space-y-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Car className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                  </div>
                  
                  <div className="w-full max-w-md space-y-4">
                    <Input
                      value={formData.plate || ''}
                      onChange={(e) => updateField('plate', e.target.value.toUpperCase())}
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      placeholder="34 ABC 123"
                      className="text-3xl sm:text-4xl md:text-5xl font-bold text-center h-20 sm:h-24 uppercase tracking-wider"
                      autoFocus
                      data-testid="input-plate"
                    />
                    
                    <p className="text-center text-sm text-muted-foreground">
                      Aracın plakasını girin
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Step: Brand */}
          {getCurrentStepId() === 'vehicle-brand' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-8">
              <Card className="p-6 sm:p-12">
                <div className="flex flex-col items-center space-y-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                  </div>
                  
                  <div className="w-full max-w-md space-y-4">
                    <Input
                      value={formData.brand || ''}
                      onChange={(e) => updateField('brand', e.target.value)}
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      placeholder="Toyota"
                      className="text-3xl sm:text-4xl md:text-5xl font-bold text-center h-20 sm:h-24"
                      autoFocus
                      data-testid="input-brand"
                    />
                    
                    <p className="text-center text-sm text-muted-foreground">
                      Araç markasını girin
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Step: Model */}
          {getCurrentStepId() === 'vehicle-model' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-8">
              <Card className="p-6 sm:p-12">
                <div className="flex flex-col items-center space-y-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Car className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                  </div>
                  
                  <div className="w-full max-w-md space-y-4">
                    <Input
                      value={formData.model || ''}
                      onChange={(e) => updateField('model', e.target.value)}
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      placeholder="Corolla"
                      className="text-3xl sm:text-4xl md:text-5xl font-bold text-center h-20 sm:h-24"
                      autoFocus
                      data-testid="input-model"
                    />
                    
                    <p className="text-center text-sm text-muted-foreground">
                      Araç modelini girin
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Step: Color */}
          {getCurrentStepId() === 'vehicle-color' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-8">
              <Card className="p-6 sm:p-12">
                <div className="flex flex-col items-center space-y-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Palette className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                  </div>
                  
                  <div className="w-full max-w-md space-y-4">
                    <Input
                      value={formData.color || ''}
                      onChange={(e) => updateField('color', e.target.value)}
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      placeholder="Beyaz"
                      className="text-3xl sm:text-4xl md:text-5xl font-bold text-center h-20 sm:h-24"
                      autoFocus
                      data-testid="input-color"
                    />
                    
                    <p className="text-center text-sm text-muted-foreground">
                      Araç rengini girin (opsiyonel)
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Step: Customer Name (Guest only) */}
          {getCurrentStepId() === 'customer-name' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-8">
              <Card className="p-6 sm:p-12">
                <div className="flex flex-col items-center space-y-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                  </div>
                  
                  <div className="w-full max-w-md space-y-4">
                    <Input
                      value={formData.customerName || ''}
                      onChange={(e) => updateField('customerName', e.target.value)}
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      placeholder="Ahmet Yılmaz"
                      className="text-3xl sm:text-4xl md:text-5xl font-bold text-center h-20 sm:h-24"
                      autoFocus
                      data-testid="input-customer-name"
                    />
                    
                    <p className="text-center text-sm text-muted-foreground">
                      Adınızı girin
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Step: Phone (Guest only) */}
          {getCurrentStepId() === 'customer-phone' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-8">
              <Card className="p-6 sm:p-12">
                <div className="flex flex-col items-center space-y-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Phone className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                  </div>
                  
                  <div className="w-full max-w-md space-y-4">
                    <Input
                      value={formatPhoneInput(formData.customerPhone || '')}
                      onChange={(e) => updateField('customerPhone', e.target.value.replace(/\D/g, ''))}
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      placeholder="0532 123 45 67"
                      type="tel"
                      className="text-3xl sm:text-4xl md:text-5xl font-bold text-center h-20 sm:h-24 tracking-wider"
                      autoFocus
                      maxLength={15}
                      data-testid="input-customer-phone"
                    />
                    
                    <p className="text-center text-sm text-muted-foreground">
                      Telefon numaranızı girin
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Step: Service Selection */}
          {getCurrentStepId() === 'services' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="text-center mb-4">
                <p className="text-lg text-muted-foreground">
                  Yapılacak hizmetleri seçin (En az 1)
                </p>
              </div>

              {/* Category Tabs */}
              <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
                <TabsList className="grid grid-cols-3 sm:grid-cols-6 gap-1 sm:gap-2 h-auto p-2 w-full">
                  <TabsTrigger value="popular" className="h-auto py-2.5 text-xs sm:text-sm">
                    ⭐ Popüler
                  </TabsTrigger>
                  <TabsTrigger value={ServiceCategory.EXTERIOR} className="h-auto py-2.5 text-xs sm:text-sm">
                    🚗 Dış
                  </TabsTrigger>
                  <TabsTrigger value={ServiceCategory.INTERIOR} className="h-auto py-2.5 text-xs sm:text-sm">
                    🧹 İç
                  </TabsTrigger>
                  <TabsTrigger value={ServiceCategory.DETAILING} className="h-auto py-2.5 text-xs sm:text-sm">
                    ✨ Detay
                  </TabsTrigger>
                  <TabsTrigger value={ServiceCategory.PACKAGE} className="h-auto py-2.5 text-xs sm:text-sm">
                    📦 Paket
                  </TabsTrigger>
                  <TabsTrigger value={ServiceCategory.EXTRA} className="h-auto py-2.5 text-xs sm:text-sm">
                    ➕ Ekstra
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredServices.map((service) => {
                  const isSelected = formData.selectedServiceIds?.includes(service.id);
                  const price = formData.type ? service.pricing[formData.type] : 0;

                  return (
                    <Card
                      key={service.id}
                      className={cn(
                        "p-6 transition-all hover:shadow-xl relative",
                        isSelected
                          ? "border-primary border-2 bg-primary/5 shadow-lg"
                          : "hover:border-primary/50"
                      )}
                      data-testid={`service-card-${service.id}`}
                    >
                      {isSelected && (
                        <div className="absolute top-3 right-3">
                          <CheckCircle2 className="w-6 h-6 text-primary" />
                        </div>
                      )}
                      
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <h3 
                            className="text-lg font-bold flex-1 pr-2 cursor-pointer"
                            onClick={() => toggleService(service.id)}
                          >
                            {service.name}
                          </h3>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 hover:bg-primary/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedServiceForDetails(service);
                              setServiceDetailDialogOpen(true);
                            }}
                            data-testid={`button-service-info-${service.id}`}
                          >
                            <Info className="w-4 h-4 text-muted-foreground hover:text-primary" />
                          </Button>
                        </div>
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => toggleService(service.id)}
                        >
                          <Badge variant="secondary" className="text-sm">
                            {formatCurrency(price)}
                          </Badge>
                          {service.isPopular && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Subscription Selector */}
              {!isGuest && activeSubscriptions.length > 0 && (
                <Card className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <Label htmlFor="use-subscription" className="text-base font-semibold text-blue-900 dark:text-blue-100">
                          Abonelik Kullan
                        </Label>
                      </div>
                      <Switch
                        id="use-subscription"
                        checked={formData.useSubscription || false}
                        onCheckedChange={(checked) => {
                          updateField('useSubscription', checked);
                          updateField('subscriptionId', checked ? activeSubscriptions[0]?.id : null);
                        }}
                        data-testid="switch-use-subscription"
                      />
                    </div>

                    {formData.useSubscription && (
                      <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {activeSubscriptions.length === 1 
                            ? 'Aboneliğiniz otomatik olarak seçildi'
                            : 'Kullanmak istediğiniz aboneliği seçin'}
                        </p>
                        
                        {activeSubscriptions.map((sub: any) => {
                          const isSelected = formData.subscriptionId === sub.id;
                          return (
                            <Card
                              key={sub.id}
                              onClick={() => updateField('subscriptionId', sub.id)}
                              className={cn(
                                "p-4 cursor-pointer transition-all hover:shadow-md",
                                isSelected 
                                  ? "border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/30" 
                                  : "border hover:border-blue-300"
                              )}
                              data-testid={`subscription-option-${sub.id}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-semibold text-sm truncate">{sub.packageName}</span>
                                    {isSelected && (
                                      <Badge variant="default" className="text-xs">Seçili</Badge>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                      <span className="text-blue-600 dark:text-blue-400">🚗</span>
                                      <span>Dış: {sub.remainingExteriorWashes}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span className="text-green-600 dark:text-green-400">🧹</span>
                                      <span>İç: {sub.remainingInteriorCleans}</span>
                                    </div>
                                    {sub.remainingDetailings > 0 && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-purple-600 dark:text-purple-400">✨</span>
                                        <span>Detay: {sub.remainingDetailings}</span>
                                      </div>
                                    )}
                                    {sub.remainingExtraServices > 0 && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-orange-600 dark:text-orange-400">➕</span>
                                        <span>Ekstra: {sub.remainingExtraServices}</span>
                                      </div>
                                    )}
                                  </div>
                                  {new Date(sub.expiresAt) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                                      ⚠️ {Math.ceil((new Date(sub.expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000))} gün kaldı
                                    </p>
                                  )}
                                </div>
                                <div className={cn(
                                  "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                                  isSelected 
                                    ? "border-blue-500 bg-blue-500" 
                                    : "border-gray-300 dark:border-gray-600"
                                )}>
                                  {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Coupon Code Input */}
              {!formData.useSubscription && (
                <Card className="p-4 border-dashed">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Tag className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">Kupon Kodu</span>
                    </div>
                    
                    {couponApplied ? (
                      <div className="flex items-center justify-between gap-3 bg-green-50 dark:bg-green-950 p-3 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="font-mono font-bold text-green-700 dark:text-green-300">
                              {formData.couponCode}
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400">
                              {formData.couponType === 'percentage' 
                                ? `%${formData.couponDiscountPercentage} indirim uygulandi`
                                : `Bedava: ${freeServiceName}`}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleRemoveCoupon}
                          className="shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                          data-testid="button-remove-coupon"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={couponInput}
                          onChange={(e) => {
                            setCouponInput(e.target.value.toUpperCase());
                            setCouponError(null);
                          }}
                          placeholder="KUPON2024"
                          className="font-mono uppercase flex-1"
                          data-testid="input-coupon-code"
                        />
                        <Button
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponInput.trim()}
                          data-testid="button-apply-coupon"
                        >
                          {couponLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Uygula"
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {couponError && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <X className="w-4 h-4" />
                        {couponError}
                      </p>
                    )}
                  </div>
                </Card>
              )}

              {/* Total Price */}
              <Card className="p-4 bg-primary/5 border-primary">
                <div className="space-y-2">
                  {(discountAmount > 0 || formData.couponFreeServiceId) && (
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span>Ara Toplam</span>
                      <span className="line-through">{formatCurrency(subtotalPrice + (formData.couponFreeServiceId ? services.find(s => s.id === formData.couponFreeServiceId)?.pricing[formData.type!] || 0 : 0))}</span>
                    </div>
                  )}
                  
                  {formData.couponType === 'percentage' && discountAmount > 0 && (
                    <div className="flex items-center justify-between text-green-600 dark:text-green-400">
                      <div className="flex items-center gap-1">
                        <Percent className="w-4 h-4" />
                        <span>Kupon Indirimi (%{formData.couponDiscountPercentage})</span>
                      </div>
                      <span>-{formatCurrency(discountAmount)}</span>
                    </div>
                  )}
                  
                  {formData.couponType === 'free_service' && freeServiceName && (
                    <div className="flex items-center justify-between text-green-600 dark:text-green-400">
                      <div className="flex items-center gap-1">
                        <Gift className="w-4 h-4" />
                        <span>Bedava Hizmet</span>
                      </div>
                      <span>{freeServiceName}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5 text-primary" />
                      <span className="font-semibold">Toplam</span>
                      {formData.useSubscription && (
                        <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
                          Abonelik
                        </Badge>
                      )}
                      {couponApplied && (
                        <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                          Kupon
                        </Badge>
                      )}
                    </div>
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(totalPrice)}
                    </span>
                  </div>
                </div>
                {formData.useSubscription && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Bu randevu aboneliginizden kullanilacaktir
                  </p>
                )}
              </Card>
            </div>
          )}

          {/* Step: Date Selection */}
          {getCurrentStepId() === 'date' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <Card className="p-6 flex justify-center">
                <Calendar
                  mode="single"
                  selected={formData.selectedDate}
                  onSelect={(date) => {
                    updateField('selectedDate', date!);
                    if (date && onDateChange) {
                      onDateChange(date);
                    }
                  }}
                  disabled={(date) => {
                    const today = startOfToday();
                    const maxDate = addDays(today, 30);
                    return date < today || date > maxDate;
                  }}
                  locale={tr}
                  className="rounded-md border"
                  data-testid="calendar-date"
                />
              </Card>
              
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Bugünden itibaren 30 gün içinde randevu alabilirsiniz
                </p>
              </div>
            </div>
          )}

          {/* Step: Time Selection */}
          {getCurrentStepId() === 'time' && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center justify-between gap-4 mb-2">
                <p className="text-lg text-muted-foreground">
                  Uygun saat seçin
                </p>
                {formData.selectedTime && (
                  <Button
                    onClick={handleNext}
                    className="shrink-0"
                    data-testid="button-confirm-time"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Onayla
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {availableSlots.map((slot) => {
                  const isSelected = formData.selectedTime === slot;
                  
                  return (
                    <Card
                      key={slot}
                      onClick={() => updateField('selectedTime', slot)}
                      className={cn(
                        "p-6 cursor-pointer transition-all hover:shadow-xl hover:scale-105 active:scale-95",
                        isSelected
                          ? "border-primary border-2 bg-primary/5 shadow-lg"
                          : "hover:border-primary/50"
                      )}
                      data-testid={`time-slot-${slot}`}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <Clock className={cn(
                          "w-6 h-6",
                          isSelected ? "text-primary" : "text-muted-foreground"
                        )} />
                        <span className={cn(
                          "text-xl font-bold",
                          isSelected && "text-primary"
                        )}>
                          {slot}
                        </span>
                        {isSelected && (
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>

              {availableSlots.length === 0 && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">
                    Seçilen tarihte uygun saat bulunamadı.
                    <br />
                    Lütfen farklı bir tarih seçin.
                  </p>
                </Card>
              )}
            </div>
          )}

          {/* Step: SMS Verification */}
          {getCurrentStepId() === 'sms-verification' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="text-center space-y-2">
                <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <MessageSquare className="w-10 h-10 text-primary" />
                </div>
                <h2 className="text-2xl font-bold">Telefon Doğrulama</h2>
                <p className="text-muted-foreground">
                  {formData.customerPhone} numarasına SMS göndereceğiz
                </p>
              </div>

              <Card className="p-6 sm:p-8 space-y-6">
                {!isSmsSent ? (
                  <div className="space-y-4">
                    <p className="text-center text-sm text-muted-foreground">
                      Telefon numaranızı doğrulamak için SMS gönder butonuna tıklayın
                    </p>
                    <Button
                      onClick={handleSendSms}
                      disabled={isSmsSending}
                      className="w-full h-12"
                      size="lg"
                      data-testid="button-send-sms"
                    >
                      {isSmsSending ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Gönderiliyor...
                        </>
                      ) : (
                        "SMS Gönder"
                      )}
                    </Button>
                    {smsError && (
                      <p className="text-sm text-destructive text-center">
                        {smsError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    <p className="text-center text-sm text-muted-foreground">
                      {formData.customerPhone} numarasına gönderilen 6 haneli kodu girin
                    </p>
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={verificationCode}
                        onChange={setVerificationCode}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    {smsError && (
                      <p className="text-sm text-destructive text-center">
                        {smsError}
                      </p>
                    )}
                  </div>
                )}
              </Card>
            </div>
          )}

        </div>
      </div>

      {/* Footer with Action Button */}
      <div className="sticky bottom-0 z-50 bg-background/95 backdrop-blur-sm border-t shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Button
            onClick={currentStep === STEPS.length ? handleComplete : handleNext}
            disabled={!canProgress()}
            className="w-full h-14 text-lg font-semibold"
            size="lg"
            data-testid="button-next"
          >
            {currentStep === STEPS.length ? (
              <>
                <Check className="w-5 h-5 mr-2" />
                Randevuyu Tamamla
              </>
            ) : (
              <>
                Devam Et
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Service Details Dialog */}
      <Dialog open={serviceDetailDialogOpen} onOpenChange={setServiceDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedServiceForDetails?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedServiceForDetails?.description && (
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground mb-1">Kısa Açıklama</h4>
                <p className="text-sm">{selectedServiceForDetails.description}</p>
              </div>
            )}
            
            {selectedServiceForDetails?.longDescription && (
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground mb-1">Detaylı Açıklama</h4>
                <p className="text-sm whitespace-pre-line">{selectedServiceForDetails.longDescription}</p>
              </div>
            )}
            
            {!selectedServiceForDetails?.description && !selectedServiceForDetails?.longDescription && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Bu hizmet için henüz açıklama eklenmemiş.
              </p>
            )}
            
            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm text-muted-foreground mb-2">Fiyatlandırma</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Binek</p>
                  <Badge variant="secondary" className="w-full justify-center">
                    {formatCurrency(selectedServiceForDetails?.pricing?.sedan || 0)}
                  </Badge>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">SUV</p>
                  <Badge variant="secondary" className="w-full justify-center">
                    {formatCurrency(selectedServiceForDetails?.pricing?.suv || 0)}
                  </Badge>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Büyük Araç</p>
                  <Badge variant="secondary" className="w-full justify-center">
                    {formatCurrency(selectedServiceForDetails?.pricing?.large || 0)}
                  </Badge>
                </div>
              </div>
            </div>
            
            {selectedServiceForDetails?.durationMinutes && (
              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm text-muted-foreground mb-1">Süre</h4>
                <p className="text-sm">Yaklaşık {selectedServiceForDetails.durationMinutes} dakika</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
